import { useCallback } from 'react';

import type { ChunkType } from '@/lib/mastra/types';
import type { AgentMessageInput, AgentStreamOptions } from '@/types/agent';
import type { AbortableMastraClient } from './use-mastra-client';
import {
  type MastraStreamConcurrency,
  type MastraStreamRequestContext,
  type MastraStreamUntilIdle,
  useMastraStreamRunner,
} from './use-mastra-stream-runner';

interface UseMastraAgentOptions {
  agentId: string;
  mastraClient: AbortableMastraClient;
}

interface UseMastraAgentReturn {
  isStreaming: boolean;
  sendMessage: (input: AgentMessageInput, options: SendAgentMessageOptions) => Promise<void>;
  stop: () => void;
}

export interface SendAgentMessageOptions {
  streamOptions?: AgentStreamOptions;
  untilIdle?: MastraStreamUntilIdle;
  concurrency?: MastraStreamConcurrency;
  onChunk: (chunk: ChunkType, context: MastraStreamRequestContext) => Promise<void> | void;
}

export function useMastraAgent({
  agentId,
  mastraClient,
}: UseMastraAgentOptions): UseMastraAgentReturn {
  const { isStreaming, runStream, stop } = useMastraStreamRunner(mastraClient);

  const sendMessage = useCallback(
    async (input: AgentMessageInput, options: SendAgentMessageOptions): Promise<void> => {
      if (typeof input === 'string' && input.trim().length === 0) {
        return;
      }

      await runStream({
        agentId,
        input,
        streamOptions: options.streamOptions,
        untilIdle: options.untilIdle,
        concurrency: options.concurrency,
        onChunk: options.onChunk,
      });
    },
    [agentId, runStream],
  );

  return {
    isStreaming,
    sendMessage,
    stop,
  };
}
